<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <v-container class="px-10 pt-8">
        <p class="subtitle-1 font-weight-bold mb-2">Update Payment Request</p>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Tenant:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">{{ tenantName }}</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Credits:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(-balance) }}
            </p>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="6">
            <v-select
              v-model="category"
              :items="categories"
              :label="category === 'rental' ? 'Rent' : 'Select Category'"
              prepend-inner-icon="mdi-tools"
              :disabled="category === 'rental'"
              dense
              outlined
              @change="changeCategory"
            >
              <template v-slot:append-item>
                <v-list-item>
                  <p class="caption grey--text">
                    Rental payment requests are automatically generated
                  </p>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
          <v-col class="py-0" cols="12" sm="6">
            <v-currency-field
              v-model="amount"
              type="number"
              label="Amount"
              prepend-inner-icon="mdi-cash"
              dense
              outlined
            >
            </v-currency-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  v-bind="attrs"
                  readonly
                  dense
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                scrollable
                @input="startDateMenu = false"
              >
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="End Date"
                  v-bind="attrs"
                  readonly
                  dense
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                scrollable
                @input="endDateMenu = false"
              >
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="12">
            <v-menu
              v-model="dueDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dueDate"
                  label="Due Date"
                  v-bind="attrs"
                  readonly
                  dense
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="dueDate"
                no-title
                scrollable
                @input="dueDateMenu = false"
              >
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12">
            <v-textarea
              v-model="description"
              name="description"
              label="Description"
              prepend-inner-icon="mdi-information"
              dense
              outlined
            >
            </v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="12">
            <v-file-input
              v-model="attachments"
              accept="image/*, application/pdf"
              label="Upload Attachments"
              prepend-icon=""
              chips
              small-chips
              multiple
              dense
              outlined
              hide-details
            >
              <template v-slot:prepend-inner>
                <v-icon small class="mt-1">mdi-paperclip</v-icon>
              </template>
            </v-file-input>
          </v-col>
        </v-row>
        <p v-if="currentAttachments.length">Attachments</p>
        <v-chip-group column multiple>
          <v-chip
            v-for="(item, index) in currentAttachments"
            :key="index"
            close
            @click:close="removeAttachment(item, index)"
          >
            {{ truncate(item.filename) }}
          </v-chip>
        </v-chip-group>
        <v-row>
          <v-col class="pb-2" cols="12" sm="12" md="12">
            <v-checkbox v-model="confirmSendEmail" class="my-0" hide-details>
              <template v-slot:label> Send email to tenant </template>
            </v-checkbox>
          </v-col>
        </v-row>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="font-weight-bold"
            color="primary darken-1"
            :disabled="!category"
            text
            @click="finish"
            >Finish</v-btn
          >
          <v-btn
            class="font-weight-bold"
            color="grey darken-1"
            text
            @click="cancel"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-container>
    </v-card>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import { dayjs } from '../../util/date'

export default {
  data() {
    return {
      paymentId: -1,
      category: '',
      categories: null,
      tenantId: -1,
      bedId: -1,
      bedName: '',
      dialog: false,
      options: {
        color: 'primary',
        width: 500,
        zIndex: 200
      },
      startDate: '',
      endDate: '',
      startDatePicker: '',
      endDatePicker: '',
      startDateMenu: false,
      endDateMenu: false,
      description: '',
      amount: 0,
      attachments: [],
      currentAttachments: [],
      loading: true,
      tenantName: '',
      balance: 0,
      leaseId: -1,
      dueDate: '',
      dueDatePicker: '',
      dueDateMenu: false,
      confirmSendEmail: false
    }
  },
  methods: {
    async open(payment) {
      this.loading = true
      this.paymentId = payment.paymentId
      this.leaseId = payment.leaseId
      this.tenantId = payment.tenantId
      this.bedId = payment.bedId
      this.bedName = payment.bedName
      this.dialog = true
      this.balance = await this.$store.dispatch(
        'units/fetchTenantBalanceAction',
        {
          tenantId: payment.tenantId
        }
      )
      this.tenantName = payment.tenantName
      this.category = payment.rawCategory
      this.startDate = dayjs(payment.startDate).format('YYYY-MM-DD')
      this.startDatePicker = dayjs(payment.startDate).format('YYYY-MM-DD')
      this.endDate = dayjs(payment.endDate).format('YYYY-MM-DD')
      this.endDatePicker = dayjs(payment.endDate).format('YYYY-MM-DD')
      this.description = payment.items[0].description
      this.amount = payment.total
      this.amountPaid = payment.total
      this.attachments = []
      this.confirmSendEmail = false
      if (payment.attachments && payment.attachments.length) {
        this.currentAttachments = payment.attachments.map((item) => {
          item.name = item.filename
          return item
        })
      }
      this.dueDate = payment.dueDate
        ? dayjs(payment.dueDate).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
      this.dueDatePicker = payment.dueDate
        ? dayjs(payment.dueDate).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')

      const categories =
        this.$store.getters['properties/getProperty']
          .payment_categories_details[0].categories
      this.categories = categories.filter(
        (category) => category.value !== 'rental'
      )

      this.loading = false
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async finish() {
      try {
        this.loading = true
        this.sanitizeDescription(this.description)

        await this.$store.dispatch('payments/updatePaymentRequest', {
          paymentId: this.paymentId,
          category: this.category,
          description: this.description,
          amount: this.amount,
          startDate: dayjs(this.startDate).format(),
          endDate: dayjs(this.endDate).format(),
          attachments: this.attachments.filter(
            (attachment) => attachment instanceof File
          ),
          leaseId: this.leaseId,
          dueDate: dayjs(this.dueDate).format(),
          currentAttachments: this.currentAttachments,
          confirmSendEmail: this.confirmSendEmail
        })

        this.dialog = false
        this.loading = false

        this.$root
          .$confirm({
            title: 'Update Payment Request',
            message: `The payment request has been updated.`,
            reconfirmOnly: true,
            confirmText: 'OK',
            options: {
              color: 'success'
            }
          })
          .then(() => {
            this.resolve(true)
          })
      } catch (err) {
        this.loading = false
        this.$root.$confirm({
          title: 'Update Payment Request',
          message: err,
          options: {
            color: 'error'
          },
          reconfirmOnly: true,
          confirmText: 'OK'
        })
      }
    },
    cancel() {
      this.dialog = false
      this.loading = false
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    changeCategory() {
      const category = this.categories.find((cat) => cat.value == this.category)
      this.description = `${category.text} bill for ${this.bedName}`
    },
    async removeAttachment(item, index) {
      if (
        await this.$root.$confirm({
          title: 'Remove attachment',
          message: `Are you sure you would like to delete ${item.filename}?`
        })
      ) {
        this.currentAttachments.splice(index, 1)
      }
    },
    truncate(input) {
      if (input.length > 7) {
        return input.substring(0, 7) + '...'
      }
      return input
    },
    sanitizeDescription() {
      const newLineAtEndOfStringRegex = /\n$/
      while (newLineAtEndOfStringRegex.test(this.description)) {
        this.description = this.description.replace(
          newLineAtEndOfStringRegex,
          ''
        )
      }
    }
  }
}
</script>

<style scoped>
p {
  margin-bottom: 0;
}
</style>
